import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, Platform } from 'react-native';

import { Box } from '@components/Restyle';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import { useWebDrawer } from '@components/Web/Drawer/WebDrawerContext';
import {
  useApproveTaskInvitationMutation,
  useJoinRequestedTaskMutation,
  useRejectTaskInvitationMutation,
} from '@graphql/generated';
import theme from '@themes/theme';

import { RestyleProps } from '../Icon/RestyleIcon';
interface CustomButtonProps {
  borderColor?: keyof typeof theme.colors;
  backgroundColor: RestyleProps['backgroundColor'];
  prefix?: React.ReactChild;
  onPress: () => void;
  variant: 'copy' | 'create';
  accessibilityLabel: string;
  suffix?: string;
  isSubmitting?: boolean;
}

const CustomButton: React.FC<CustomButtonProps> = ({
  borderColor,
  backgroundColor,
  prefix,
  onPress,
  variant,
  suffix,
  accessibilityLabel,
  isSubmitting = false,
}) => {
  return (
    <Button
      disabled={isSubmitting}
      borderColor={borderColor}
      backgroundColor={backgroundColor}
      prefix={prefix}
      variant={variant}
      onPress={onPress}
      isSmall
      centerIcon={!suffix}
      height={32}
      accessibilityLabel={accessibilityLabel}
      prefixMarginRight={Platform.OS === 'web' ? 'xs' : 'xxs'}
      children={
        isSubmitting ? (
          <Box ml='m'>
            <ActivityIndicator color={theme.colors.textPrimary} size={16} />
          </Box>
        ) : (
          suffix || ''
        )
      }></Button>
  );
};

interface AcceptDeclineButtonsProps {
  requestId?: string;
  suffix?: boolean;
  toggleAcceptDecline?: () => void;
  isJoinTask?: boolean;
  taskId?: string;
}

const AcceptDeclineButtons: React.FC<AcceptDeclineButtonsProps> = ({
  requestId,
  suffix,
  toggleAcceptDecline,
  isJoinTask = false,
  taskId,
}) => {
  const { t } = useTranslation('models');
  const { setIdTaskDetailOpen, setIsTaskWebPanelOpen } = useWebDrawer();
  const [isSubmittingApprove, setIsSubmittingApprove] =
    useState<boolean>(false);
  const [isSubmittingReject, setIsSubmittingReject] = useState<boolean>(false);

  const toggleAcceptDeclineRef = useRef(toggleAcceptDecline);
  toggleAcceptDeclineRef.current = toggleAcceptDecline;

  const [joinRequestedTask, { data: joinRequestedTaskData, error: joinRequestedTaskError }] =
    useJoinRequestedTaskMutation({
      fetchPolicy: 'network-only',
      refetchQueries: ['listMessages', 'listTasks', 'getTask'],
    });

  useEffect(() => {
    if (!joinRequestedTaskData) return;
    setIsSubmittingApprove(false);
    toggleAcceptDeclineRef.current?.();
    if (Platform.OS === 'web' && taskId && typeof window !== 'undefined') {
      window.sessionStorage.setItem('newly_joined_task_id', taskId);
      setIdTaskDetailOpen(taskId);
      setIsTaskWebPanelOpen(true);
    }
  }, [joinRequestedTaskData]);

  useEffect(() => {
    if (!joinRequestedTaskError) return;
    setIsSubmittingApprove(false);
  }, [joinRequestedTaskError]);

  const [approveTaskInvitation, { data: approveTaskInvitationData, error: approveTaskInvitationError }] =
    useApproveTaskInvitationMutation({
      fetchPolicy: 'network-only',
      refetchQueries: ['listMessages', 'listTasks', 'getTask'],
    });

  useEffect(() => {
    if (!approveTaskInvitationData) return;
    setIsSubmittingApprove(false);
    toggleAcceptDeclineRef.current?.();
  }, [approveTaskInvitationData]);

  useEffect(() => {
    if (!approveTaskInvitationError) return;
    setIsSubmittingApprove(false);
  }, [approveTaskInvitationError]);

  const [rejectTaskInvitation, { data: rejectTaskInvitationData, error: rejectTaskInvitationError }] =
    useRejectTaskInvitationMutation({
      fetchPolicy: 'network-only',
      refetchQueries: ['listMessages', 'listTasks', 'getTask'],
    });

  useEffect(() => {
    if (!rejectTaskInvitationData) return;
    setIsSubmittingReject(false);
    toggleAcceptDeclineRef.current?.();
  }, [rejectTaskInvitationData]);

  useEffect(() => {
    if (!rejectTaskInvitationError) return;
    setIsSubmittingReject(false);
  }, [rejectTaskInvitationError]);

  const onAcceptOrDecline = (status: string) => {
    if (isJoinTask) {
      setIsSubmittingApprove(true);
      joinRequestedTask({
        variables: {
          userRequestId: requestId || '',
        },
      });
    } else if (status === 'approved') {
      setIsSubmittingApprove(true);
      approveTaskInvitation({
        variables: {
          id: requestId || '',
        },
      });
    } else if (status === 'rejected') {
      setIsSubmittingReject(true);
      rejectTaskInvitation({
        variables: {
          id: requestId || '',
        },
      });
    }
  };

  return isJoinTask ? (
    <Box width={suffix ? 125 : 51}>
      <CustomButton
        borderColor='black'
        backgroundColor='black'
        variant='create'
        suffix={suffix ? 'Join Task' : ''}
        prefix={<Icon variant='s' name='CheckCircle' color='white' />}
        onPress={() => onAcceptOrDecline('approved')}
        accessibilityLabel={t('shared:accept')}
        isSubmitting={isSubmittingApprove}
      />
    </Box>
  ) : (
    <>
      <Box width={suffix ? 125 : 51} marginRight='xs'>
        <CustomButton
          borderColor='black'
          backgroundColor='white'
          variant='copy'
          prefix={<Icon variant='s' name='X' color='black' />}
          suffix={suffix ? 'Decline' : ''}
          onPress={() => onAcceptOrDecline('rejected')}
          accessibilityLabel={t('shared:decline')}
          isSubmitting={isSubmittingReject}
        />
      </Box>
      <Box width={suffix ? 125 : 51}>
        <CustomButton
          borderColor='black'
          backgroundColor='black'
          variant='create'
          suffix={suffix ? 'Approve' : ''}
          prefix={<Icon variant='s' name='CheckCircle' color='white' />}
          onPress={() => onAcceptOrDecline('approved')}
          accessibilityLabel={t('shared:accept')}
          isSubmitting={isSubmittingApprove}
        />
      </Box>
    </>
  );
};

export default AcceptDeclineButtons;
